import { useState } from "react";
import { Users, UserPlus, Trash2, ShieldCheck, Shield } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { usePageTitle } from "@/hooks/use-page-title";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  useMembers,
  useUpdateMemberRole,
  useRemoveMember,
} from "@/hooks/queries/use-organization";
import type { OrganizationMember } from "@/lib/api/organization";
import { InviteUserModal } from "@/components/admin/InviteUserModal";
import { InvitationList } from "@/components/admin/InvitationList";
import { WriteGuard } from "@/components/billing/write-guard";
import { CompanyDomains } from "@/components/organization/company-domains";
import { OrganizationNameSetting } from "@/components/organization/organization-name";
import { JoinRequests } from "@/components/organization/join-requests";
import { MemberTeams } from "@/components/organization/member-teams";
import { cn } from "@/lib/utils";

/**
 * Organization members screen (ENG-211).
 *
 * Admin-facing: lists the members of the caller's own organization with role
 * and status, plus the write actions (invite, promote, demote, remove).
 *
 * Authority model: the server enforces org-admin on every route this page
 * touches. The members query itself is admin-only, so a plain member never
 * sees rows — they see the server's 403 message — and consequently never sees
 * the write controls either. That makes "hide actions from non-admins" fall
 * out of the data flow rather than a client-side role check that could drift
 * from the server's definition of admin.
 *
 * Refusals the server can still return to a real admin (self-edit, last
 * admin) are rendered inline next to the offending row, verbatim, because the
 * server's message names the actual rule that fired.
 */
export function OrganizationMembersPage() {
  usePageTitle("Members");
  const isMobile = useIsMobile();
  const { data: members, isLoading, error } = useMembers();
  const [inviteOpen, setInviteOpen] = useState(false);

  return (
    <div className="pt-4 px-4 md:px-0">
      <div className="glass-card rounded-2xl p-4 md:p-6">
        <div className="mb-6 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-muted-foreground" />
            <h1 className="text-lg font-medium">Organization Members</h1>
          </div>
          {/* Only rendered once the admin-only query has produced rows, so a
              plain member (403, no rows) never sees the invite affordance. */}
          {members && (
            <WriteGuard>
              <Button
                size="sm"
                onClick={() => {
                  setInviteOpen(true);
                }}
              >
                <UserPlus className="mr-1 h-4 w-4" />
                Invite
              </Button>
            </WriteGuard>
          )}
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            Loading members...
          </div>
        )}

        {error && (
          <div className="flex items-center justify-center py-8 text-destructive">
            {error instanceof Error
              ? error.message
              : "Failed to load members"}
          </div>
        )}

        {members && (
          <>
            <div className="mb-8">
              <OrganizationNameSetting />
            </div>

            <div className="mb-6 empty:hidden">
              <JoinRequests />
            </div>

            <ul className="divide-y divide-border">
              {members.map((member) => (
                <MemberRow
                  key={member.id}
                  member={member}
                  isMobile={isMobile}
                />
              ))}
            </ul>

            {members.length === 1 && (
              <div className="mt-6 rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                You are the only member of this organization so far. Invite
                teammates and they will appear here once they accept.
              </div>
            )}

            <div className="mt-8">
              <h2 className="mb-4 text-base font-medium">Invitations</h2>
              {/* ENG-E38 component reused as-is: invitations are scoped to
                  the caller's organization server-side. */}
              <InvitationList />
            </div>

            <div className="mt-8">
              <CompanyDomains />
            </div>
          </>
        )}
      </div>

      <InviteUserModal
        open={inviteOpen}
        onClose={() => {
          setInviteOpen(false);
        }}
      />
    </div>
  );
}

function MemberRow({
  member,
  isMobile,
}: {
  member: OrganizationMember;
  isMobile: boolean;
}) {
  const updateRole = useUpdateMemberRole();
  const removeMember = useRemoveMember();
  // The row's own last refusal (self-edit, last admin, ...). Kept per-row so
  // a refusal on one member doesn't decorate every other row, and cleared on
  // the next attempt so stale messages don't outlive their cause.
  const [rowError, setRowError] = useState<string | null>(null);

  const isAdmin = member.orgRole === "admin";
  const busy = updateRole.isPending || removeMember.isPending;

  const handleRoleChange = () => {
    setRowError(null);
    updateRole.mutate(
      { id: member.id, role: isAdmin ? "member" : "admin" },
      {
        onError: (err: Error) => {
          setRowError(err.message);
        },
      }
    );
  };

  const handleRemove = () => {
    setRowError(null);
    removeMember.mutate(member.id, {
      onError: (err: Error) => {
        setRowError(err.message);
      },
    });
  };

  return (
    <li
      className={cn(
        "py-3",
        isMobile ? "flex flex-col gap-2" : "flex items-center justify-between"
      )}
    >
      <div className="min-w-0">
        <div className="truncate font-medium">{member.name}</div>
        <div className="truncate text-sm text-muted-foreground">
          {member.email}
        </div>
        <MemberTeams member={member} />
        {rowError && (
          <p
            role="alert"
            className="mt-1 text-sm text-destructive"
            data-testid="member-row-error"
          >
            {rowError}
          </p>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Badge variant={isAdmin ? "default" : "secondary"}>
          {isAdmin ? "Admin" : "Member"}
        </Badge>
        <Badge
          variant="outline"
          className={cn(
            member.isActive ? "text-emerald-500" : "text-muted-foreground"
          )}
        >
          {member.isActive ? "Active" : "Inactive"}
        </Badge>
        <WriteGuard>
          <Button
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={handleRoleChange}
            aria-label={
              isAdmin
                ? `Demote ${member.name} to member`
                : `Promote ${member.name} to admin`
            }
          >
            {isAdmin ? (
              <>
                <Shield className="mr-1 h-4 w-4" />
                Demote
              </>
            ) : (
              <>
                <ShieldCheck className="mr-1 h-4 w-4" />
                Make admin
              </>
            )}
          </Button>
        </WriteGuard>
        <WriteGuard>
          <Button
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={handleRemove}
            aria-label={`Remove ${member.name} from the organization`}
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </WriteGuard>
      </div>
    </li>
  );
}
